import React from 'react'
import clsx from 'clsx'
import './list.scss'
import styles from './list.module.scss'
import * as PropTypes from 'prop-types'
import { flexRender } from '@tanstack/react-table'
import Typography from '../Typography'

const List = ({
  table,
  config: {
    className = '',
    skin = 'default',
    showHeader = true,
    showFooter = false,
    RenderSubComponent = () => null,
  },
  EmptyComponent = null,
}) =>
  table.getRowCount() === 0 && EmptyComponent ? (
    <EmptyComponent />
  ) : (
    <table className={clsx(styles[skin], className)}>
      {showHeader && (
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <>
              <tr key={'headerGroup-' + headerGroup.id}>
                {headerGroup.headers.map((header, i) => (
                  <th
                    key={'header-th-' + i}
                    colSpan={header.colSpan}
                    role={'columnheader'}
                    style={{
                      width:
                        header.column.getSize() !== 0
                          ? header.column.getSize()
                          : undefined,
                    }}
                    className={header.column.columnDef.className}
                  >
                    {header.isPlaceholder ? null : (
                      <Typography
                        noMargin
                        component={'span'}
                        variant={'Body-20'}
                        className={
                          header.column.getCanSort()
                            ? 'cursor-pointer select-none'
                            : ''
                        }
                        onClick={header.column.getToggleSortingHandler()}
                        title={
                          header.column.getCanSort()
                            ? header.column.getNextSortingOrder() === 'asc'
                              ? 'Sort ascending'
                              : header.column.getNextSortingOrder() === 'desc'
                              ? 'Sort descending'
                              : 'Clear sort'
                            : undefined
                        }
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                        {/*{{*/}
                        {/*  asc: ' 🔼',*/}
                        {/*  desc: ' 🔽',*/}
                        {/*}[header.column.getIsSorted()] ?? null}*/}
                      </Typography>
                    )}
                  </th>
                ))}
              </tr>
              <tr role={'spacer'} key={'spacer-tr-' - headerGroup.id}>
                <th
                  key={'spacer-th-' - headerGroup.id}
                  colSpan={headerGroup.headers.length}
                />
              </tr>
            </>
          ))}
        </thead>
      )}
      <tbody>
        {table.getRowModel().rows.map((row, i) => (
          <React.Fragment key={'tbody-row-fragment-' + i}>
            {i !== 0 && (
              <tr role={'spacer'} key={'tbody-row-spacer' - i}>
                <td
                  colSpan={row.getVisibleCells().length}
                  key={'spacer-cell-' + i}
                />
              </tr>
            )}
            <tr
              key={'tbody-row-' + row.id}
              role={'row'}
              className={row.getIsExpanded() ? 'row-expanded' : ''}
            >
              {row.getVisibleCells().map((cell) => (
                <td
                  key={'tbody-row-' + row.id + '-cell-' + cell.id}
                  role={'cell'}
                  style={{
                    width:
                      cell.column.getSize() !== 0
                        ? cell.column.getSize()
                        : undefined,
                  }}
                  className={cell.column.columnDef.className}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
            {row.getIsExpanded() && (
              <RenderSubComponent
                key={'tbody-row-' + row.id + '-subrow-' + row.id}
                row={row}
                table={table}
                keyRowPrefix={'tbody-row-' + row.id + '-subrow'}
              />
            )}
          </React.Fragment>
        ))}
      </tbody>
      {showFooter && (
        <tfoot>
          {table.getFooterGroups().map((footerGroup, i) => (
            <tr role={'row'} key={'footer-' + i}>
              {footerGroup.headers.map((header, j) => (
                <td
                  key={'footer-td-' + j}
                  colSpan={header.colSpan}
                  role={'columnheader'}
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tfoot>
      )}
    </table>
  )

List.propTypes = {
  table: PropTypes.object,
  config: PropTypes.object,
  EmptyComponent: PropTypes.elementType,
}

export default List
